import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { useAuth } from './auth/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import LoginScreen from './components/LoginScreen';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
// DailyLog moved to IklimPage
import MobileBottomNav from './components/MobileBottomNav';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdateBanner from './components/PWAUpdateBanner';
import KeyboardShortcutsHint from './components/KeyboardShortcutsHint';
import { navigationSchema } from './config/navigationSchema';
import CommandPalette from './components/CommandPalette';
import {
  Activity, Archive, BarChart2, BarChart3, Bell, BookOpen, Calendar,
  CheckSquare, Eye, FlaskConical, HelpCircle, History, Layers, LineChart,
  MoreHorizontal, Package, Settings, SlidersHorizontal, Sprout, Thermometer, Zap,
  type LucideIcon,
} from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  Activity, Archive, BarChart2, BarChart3, Bell, BookOpen, Calendar,
  CheckSquare, Eye, FlaskConical, History, Layers, LineChart,
  Package, Settings, SlidersHorizontal, Sprout, Thermometer, Zap,
};

// 🚀 Lazy-loaded components — route bazlı ve Recharts içeren bileşenler
const SistemPage      = lazy(() => import('./pages/SistemPage'));
const AyarlarPage     = lazy(() => import('./pages/AyarlarPage'));
const UyarilarPage    = lazy(() => import('./pages/UyarilarPage'));
const KomutaPage      = lazy(() => import('./pages/KomutaPage'));
const OperasyonPage   = lazy(() => import('./pages/OperasyonPage'));
const IklimPage       = lazy(() => import('./pages/IklimPage'));
const GrowTrackerPage = lazy(() => import('./components/GrowTracker'));
const AnalyticsPage   = lazy(() => import('./components/AnalyticsPage'));
const AIPage          = lazy(() => import('./pages/AIPage'));
import { useAppStore } from './store';
import { useAppMetrics, SensorDataProvider } from './hooks';

import SystemDaemon from './components/SystemDaemon';
import OfflineBanner from './components/OfflineBanner';
import { useTelegramBot } from './hooks/useTelegramBot';
import { useDailyReport } from './hooks/useDailyReport';
import LoadingSplash from './components/MyceliumSplash';
import { LogOut } from 'lucide-react';
import React, { memo } from 'react';


export default function App() {
  const { session, loading: authLoading, bypassed } = useAuth();
  const [splashDone, setSplashDone] = useState(false);

  const isAuthed = !authLoading && (bypassed || !!session);

  // Splash geçti ama auth hâlâ yükleniyor (nadir — bypass localhost'ta anında resolve eder)
  if (splashDone && authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#070809' }}>
        <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
      </div>
    );
  }

  if (splashDone && !isAuthed) {
    return <LoginScreen />;
  }

  // Splash + içerik aynı anda render edilir; splash position:fixed üstte durur,
  // arkada uygulama yükleniyor → splash kapanınca içerik hazır.
  return (
    <>
      {!splashDone && <LoadingSplash onDone={() => setSplashDone(true)} />}
      {isAuthed && (
        <ErrorBoundary>
          <Toaster position="top-right" />
          <SensorDataProvider>
            <AppInner initialLoading={false} />
          </SensorDataProvider>
        </ErrorBoundary>
      )}
    </>
  );
}

function AppInner({ initialLoading = true }: { initialLoading?: boolean }) {
  const { logout, session, bypassed } = useAuth();
  const metrics = useAppMetrics();
  
  // Enable keyboard navigation accessibility
  useKeyboardNavigation();

  const [dahaMenuOpen, setDahaMenuOpen] = useState(false);
  const dahaMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dahaMenuOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (dahaMenuRef.current && !dahaMenuRef.current.contains(e.target as Node)) {
        setDahaMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [dahaMenuOpen]);

  // Granular selectors — sadece ilgili slice değişince re-render tetiklenir
  const readings             = useAppStore(s => s.readings);
  const current              = useAppStore(s => s.current);
  const devices              = useAppStore(s => s.devices);
  const dailySummaries       = useAppStore(s => s.dailySummaries);
  const alerts               = useAppStore(s => s.alerts);
  const healthScore          = useAppStore(s => s.healthScore);
  const unreadAlertCount     = useAppStore(s => s.unreadAlertCount);
  const growBags             = useAppStore(s => s.growBags);
  const lastUpdated          = useAppStore(s => s.lastUpdated);
  const alertEmailSettings   = useAppStore(s => s.alertEmailSettings);
  const alertNotifSettings   = useAppStore(s => s.alertNotifSettings);
  const deviceEvents         = useAppStore(s => s.deviceEvents);
  const dataSourceLabel      = useAppStore(s => s.dataSourceLabel);

  // selectDay is now used by IklimPage
  const allDevicesOff        = useAppStore(s => s.allDevicesOff);
  const restoreDevices       = useAppStore(s => s.restoreDevices);

  const {
    activeStage,
    sensorConfidence
  } = metrics;

  useTelegramBot({
    current,
    devices,
    alerts,
    activeStage,
    healthScore: healthScore?.overall ?? null,
    sensorConfidence,
    growBags,
    dailySummaries,
    deviceEvents,
  });

  const [loading, setLoading] = useState(initialLoading);
  const location = useLocation();
  const navigate = useNavigate();
  const page = location.pathname === '/uretim'    ? 'uretim'    :
               location.pathname === '/iklim'      ? 'iklim'     :
               location.pathname === '/analitik'   ? 'analitik'  :
               location.pathname === '/operasyon'  ? 'operasyon' :
               location.pathname === '/sistem'     ? 'sistem'    :
               location.pathname === '/ayarlar'    ? 'ayarlar'   :
               location.pathname === '/uyarilar'   ? 'uyarilar'  :
               location.pathname === '/ai'         ? 'ai'        : 'dashboard';

  useEffect(() => {
    const titles: Record<string, string> = {
      'dashboard':  'Komuta — Gurme Mantar',
      'uretim':     'Üretim — Gurme Mantar',
      'iklim':      'İklim — Gurme Mantar',
      'analitik':   'Analitik — Gurme Mantar',
      'operasyon':  'Operasyon — Gurme Mantar',
      'sistem':     'Sistem — Gurme Mantar',
      'ayarlar':    'Ayarlar — Gurme Mantar',
      'uyarilar':   'Uyarılar — Gurme Mantar',
      'ai':         'AI Asistan — Gurme Mantar',
    };
    document.title = titles[page] ?? 'Gurme Mantar';
  }, [page]);



  // localStorage doluluk uyarısı
  useEffect(() => {
    const handler = () => {
      toast.error('Depolama alanı doldu: bazı veriler kaydedilemeyebilir. Tarayıcı verilerini temizleyin.', {
        duration: 10000,
        id: 'storage-full',
      });
    };
    window.addEventListener('gmc:storage-full', handler);
    return () => window.removeEventListener('gmc:storage-full', handler);
  }, []);

  // Supabase sync hatası bildirimi
  useEffect(() => {
    const handler = () => {
      toast.error('Bulut senkronizasyonu başarısız: veriler yerel olarak kaydedildi.', {
        duration: 6000,
        id: 'sync-error',
      });
    };
    window.addEventListener('gmc:sync-error', handler);
    return () => window.removeEventListener('gmc:sync-error', handler);
  }, []);

  const devicesOffPendingRef = useRef(false);
  const devicesOffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAllDevicesOffShortcut = useCallback(() => {
    if (devicesOffPendingRef.current) {
      if (devicesOffTimerRef.current) clearTimeout(devicesOffTimerRef.current);
      devicesOffPendingRef.current = false;
      allDevicesOff();
      toast.dismiss('devices-off-confirm');
      return;
    }
    devicesOffPendingRef.current = true;
    toast('Tüm cihazları durdurmak için tekrar "." bas', { id: 'devices-off-confirm', duration: 3000 });
    devicesOffTimerRef.current = setTimeout(() => {
      devicesOffPendingRef.current = false;
      toast.dismiss('devices-off-confirm');
    }, 3000);
  }, [allDevicesOff]);

  const [showShortcuts, setShowShortcuts] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Keep current time updated for pure rendering of relative time indicators
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 15000);
    return () => clearInterval(timer);
  }, []);

  // Command palette toggle (Cmd+K on Mac, Ctrl+K on Windows/Linux)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = /Mac|iPhone|iPad/i.test(
        (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
        ?? navigator.userAgent
      );
      const isTrigger = isMac ? (e.metaKey && e.key.toLowerCase() === 'k') : (e.ctrlKey && e.key.toLowerCase() === 'k');
      if (isTrigger) {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const shortcutMap = useMemo(() => {
    const map: Record<string, () => void> = {};
    
    // Schema-driven navigation shortcuts
    navigationSchema.filter(item => item.kbd).forEach(item => {
      const kbd = item.kbd!;
      const action = () => {
        if (item.tabId) {
          navigate(`${item.path}?tab=${item.tabId}`);
        } else {
          navigate(item.path);
        }
      };
      map[kbd] = action;
      if (/^[a-zA-Z]$/.test(kbd)) {
        map[kbd.toLowerCase()] = action;
        map[kbd.toUpperCase()] = action;
      }
    });

    // Custom non-navigation actions
    map['.'] = handleAllDevicesOffShortcut;
    map['r'] = () => restoreDevices();
    map['R'] = () => restoreDevices();
    map['?'] = () => setShowShortcuts(v => !v);
    map['Escape'] = () => {
      if (commandPaletteOpen) { setCommandPaletteOpen(false); return; }
      if (showShortcuts) { setShowShortcuts(false); return; }
    };

    return map;
  }, [commandPaletteOpen, showShortcuts, handleAllDevicesOffShortcut, restoreDevices, navigate]);

  useKeyboardShortcuts(shortcutMap);

  useDailyReport({
    bags: growBags,
    alerts,
    todayReadings: readings,
    healthScore: healthScore?.overall ?? 0,
    healthLabel: healthScore?.label ?? '',
    notifSettings: alertNotifSettings,
    emailTo: alertEmailSettings.enabled ? alertEmailSettings.email : '',
    devices,
  });

  if (!current || !healthScore) {
    return (
      <>
        <SystemDaemon />
        <div className="min-h-screen p-4 md:p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="skeleton h-10 w-64" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="skeleton h-36 rounded-[14px]" />
              <div className="skeleton h-36 rounded-[14px]" />
              <div className="skeleton h-36 rounded-[14px]" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-8 skeleton h-80 rounded-[14px]" />
              <div className="lg:col-span-4 skeleton h-80 rounded-[14px]" />
            </div>
          </div>
        </div>
      </>
    );
  }



  return (
    <div className={`min-h-screen relative overflow-x-hidden ${page === 'ai' ? 'h-screen overflow-y-hidden p-3 sm:p-4 md:p-6' : 'p-3 sm:p-4 md:p-8 md:pb-8 pb-nav-safe'}`}>
      <a href="#main-content" className="skip-to-main">İçeriğe geç</a>
      <SystemDaemon />
      <OfflineBanner />
      {loading && <LoadingSplash onDone={() => setLoading(false)} />}

      <div style={page === 'ai' ? { height: 'calc(100% - 20px)' } : undefined} className={`max-w-7xl mx-auto relative z-10 ${page === 'ai' ? 'flex flex-col space-y-3' : 'space-y-8'}`}>
        {/* ── Header ── */}
        <header className="flex items-center justify-between gap-3 mb-2">
          {/* Left: logo + labeled nav + stage badge */}
          <div className="flex items-center gap-4 min-w-0">
            <h1 className="display text-[26px] sm:text-[36px] leading-none whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
              Gurme <span className="italic-display" style={{ color: 'var(--text-2)', fontSize: '0.88em' }}>Komuta</span>
            </h1>
            <div className="hidden md:block w-px h-6 flex-shrink-0" style={{ background: 'var(--line-strong)' }} />
            <nav aria-label="Ana araç çubuğu" className="hidden md:flex items-center gap-1">
              {['1', '3', '2', '4']
                .map(kbd => navigationSchema.find(item => item.kbd === kbd))
                .filter((item): item is typeof navigationSchema[number] => !!item)
                .map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = ICON_MAP[item.icon] ?? HelpCircle;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (item.tabId) {
                          navigate(`${item.path}?tab=${item.tabId}`);
                        } else {
                          navigate(item.path);
                        }
                      }}
                      title={`${item.label} [${item.kbd}]`}
                      className={`h-10 flex items-center gap-1.5 px-2.5 rounded-xl transition-all cursor-pointer ${
                        isActive ? 'border' : 'border border-transparent hover:bg-white/[0.05]'
                      }`}
                      style={isActive ? {
                        background: 'var(--ok-bg)',
                        borderColor: 'rgba(110,231,183,0.20)',
                        color: 'var(--ok)',
                      } : { color: 'var(--text-3)' }}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span style={{ fontSize: 'var(--text-nav)', fontWeight: 600, letterSpacing: '-0.005em' }}>{item.label}</span>
                    </button>
                  );
                })}
            </nav>
          </div>

          {/* Right: toolbar group + alerts + user */}
          <div className="flex items-center gap-2 flex-shrink-0">

            {/* Daha / Diğer Menüsü */}
            <div className="hidden md:block relative" ref={dahaMenuRef}>
              <div className="flex items-center gap-0.5 px-1 rounded-xl" style={{
                background: ['ayarlar', 'sistem', 'ai', 'operasyon'].includes(page) ? 'var(--ok-bg)' : 'rgba(255,255,255,0.02)',
                border: ['ayarlar', 'sistem', 'ai', 'operasyon'].includes(page) ? '1px solid rgba(110,231,183,0.20)' : '1px solid var(--line)'
              }}>
                <HeaderIconButton
                  onClick={() => setDahaMenuOpen(v => !v)}
                  title="Daha Fazla Seçenek"
                >
                  <MoreHorizontal className="w-4 h-4" style={{ color: ['ayarlar', 'sistem', 'ai', 'operasyon'].includes(page) ? 'var(--ok)' : undefined }} />
                </HeaderIconButton>
              </div>

              {/* Glassmorphic Dropdown */}
              {dahaMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 rounded-xl z-[150] p-1.5 transition-all animate-popup-desktop"
                  style={{
                    background: 'rgba(18, 20, 25, 0.96)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid var(--line-strong)',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  <div className="space-y-0.5">
                    {[
                      { id: 'ayarlar', label: 'Ayarlar', path: '/ayarlar', icon: SlidersHorizontal, kbd: 'S' },
                      { id: 'sistem', label: 'Sistem', path: '/sistem?tab=durum', icon: Activity, kbd: '6' },
                      { id: 'ai', label: 'AI Asistan', path: '/ai', icon: Sprout, kbd: '8' },
                      { id: 'operasyon', label: 'Operasyon', path: '/operasyon', icon: CheckSquare, kbd: '5' },
                    ].map(item => {
                      const isActive = location.pathname === item.path.split('?')[0];
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            setDahaMenuOpen(false);
                            navigate(item.path);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left font-semibold cursor-pointer transition-all"
                          style={{
                            fontSize: 'var(--text-nav)',
                            color: isActive ? 'var(--ok)' : 'var(--text-3)',
                            background: isActive ? 'rgba(110,231,183,0.06)' : 'transparent',
                          }}
                          onMouseEnter={e => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                              e.currentTarget.style.color = 'var(--text-primary)';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isActive) {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = 'var(--text-3)';
                            }
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="w-3.5 h-3.5" style={{ color: isActive ? 'var(--ok)' : undefined }} />
                            <span>{item.label}</span>
                          </span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded uppercase border border-white/[0.06]" style={{ background: 'rgba(255,255,255,0.01)', color: 'var(--text-4)' }}>
                            {item.kbd}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Alerts button — text pill when unread */}
            <button
              onClick={() => navigate('/uyarilar')}
              title="Uyarılar & Olaylar [A]"
              className="hidden md:flex items-center gap-2 h-10 px-3 rounded-xl border transition-all cursor-pointer"
              style={{
                color: unreadAlertCount > 0 ? 'var(--crit)' : 'var(--text-3)',
                borderColor: unreadAlertCount > 0 ? 'rgba(252,165,165,0.25)' : 'var(--line)',
                background: unreadAlertCount > 0 ? 'var(--crit-bg)' : 'transparent',
              }}
              onMouseEnter={e => { if (!unreadAlertCount) { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; } }}
              onMouseLeave={e => { if (!unreadAlertCount) { e.currentTarget.style.color = 'var(--text-3)'; e.currentTarget.style.background = 'transparent'; } }}
            >
              <Bell className="w-4 h-4" />
              {unreadAlertCount > 0 && (
                <span className="text-[12px] font-semibold">{unreadAlertCount} uyarı</span>
              )}
            </button>

            {/* User menu: shortcuts + logout */}
            <button
              onClick={() => setShowShortcuts(true)}
              title="Klavye Kısayolları [?]"
              className="hidden md:flex w-9 h-9 items-center justify-center rounded-xl border transition-all cursor-pointer"
              style={{ color: 'var(--text-3)', borderColor: 'var(--line)', background: 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-3)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <span className="mono text-[10.5px] font-bold">?</span>
            </button>
            {!bypassed && session && (
              <button
                onClick={logout}
                title={`Çıkış Yap (${session.email})`}
                className="hidden md:flex w-9 h-9 items-center justify-center rounded-xl border border-transparent hover:border-red-500/20 transition-all cursor-pointer"
                style={{ color: 'var(--text-3)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--crit)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-3)')}
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </header>

        {/* InfoBar hidden — kabin ortamı ve hava verisi sistemde tutulmakta */}
        {/* {outdoorWeather && <InfoBar weather={outdoorWeather} ambientReading={ambientReading} />} */}

        <main id="main-content" className={page === 'ai' ? 'flex-1 min-h-0' : undefined}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={
            <ErrorBoundary compact label="Gösterge Paneli">
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center">
                  <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
                </div>
              }>
                <KomutaPage />
              </Suspense>
            </ErrorBoundary>
        } />

        <Route path="/uretim" element={
          <ErrorBoundary compact label="Üretim">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <GrowTrackerPage />
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/iklim" element={
          <ErrorBoundary compact label="İklim">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <IklimPage />
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/analitik" element={
          <ErrorBoundary compact label="Analitik">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <div className="animate-page-enter">
                <AnalyticsPage />
              </div>
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/operasyon" element={
          <ErrorBoundary compact label="Operasyon">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <OperasyonPage />
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/sistem" element={
          <ErrorBoundary compact label="Sistem">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <SistemPage />
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/ayarlar" element={
          <ErrorBoundary compact label="Ayarlar">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <AyarlarPage />
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/uyarilar" element={
          <ErrorBoundary compact label="Uyarılar">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <UyarilarPage />
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/ai" element={
          <ErrorBoundary compact label="AI Asistan">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <span className="w-8 h-8 border-2 spin-ok rounded-full animate-spin" />
              </div>
            }>
              <AIPage />
            </Suspense>
          </ErrorBoundary>
        } />

        <Route path="/hakkimda" element={<Navigate to="/ai" replace />} />

        {/* Eski URL'lerden redirect */}
        <Route path="/grow-tracker"  element={<Navigate to="/uretim"   replace />} />
        <Route path="/visualization" element={<Navigate to="/iklim"    replace />} />
        <Route path="/analytics"     element={<Navigate to="/analitik" replace />} />
        <Route path="/operations"    element={<Navigate to="/operasyon" replace />} />
        <Route path="/system-status" element={<Navigate to="/sistem"   replace />} />
        <Route path="/agent-control" element={<Navigate to="/sistem"   replace />} />
        <Route path="/system"        element={<Navigate to="/sistem"   replace />} />
        <Route path="/planning"      element={<Navigate to="/operasyon" replace />} />
        <Route path="*"              element={<Navigate to="/"         replace />} />
        </Routes>
        </main>

        {page !== 'ai' && (
          <footer className="py-6 border-t" style={{ borderColor: 'var(--line)', background: 'rgba(255,255,255,0.01)' }}>
            <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Sol Taraf: Premium Markalama */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2.5 text-[11px]" style={{ fontFamily: "'Geist', sans-serif" }}>
                <span className="font-semibold tracking-[0.15em] text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400" style={{ fontWeight: 600 }}>
                  GURME KOMUTA
                </span>
                <span style={{ color: 'var(--text-4)' }}>·</span>
                <span style={{ color: 'var(--text-3)' }}>© 2026 Biyoteknolojik Akıllı Tarım</span>
                <span style={{ color: 'var(--text-4)' }}>·</span>
                <span className="text-[10px] uppercase tracking-wider text-emerald-400/40 font-semibold" style={{ letterSpacing: '0.05em' }}>
                  Otonom Sistem
                </span>
              </div>

              {/* Sağ Taraf: Canlı Bağlantı ve Son Veri Bilgisi */}
              {(() => {
                const staleSec = current?.timestamp
                  ? Math.max(0, Math.round((now - new Date(current.timestamp).getTime()) / 1000))
                  : null;
                const ageLabel =
                  staleSec == null      ? null
                  : staleSec < 60       ? `${staleSec} sn`
                  : staleSec < 3600     ? `${Math.round(staleSec / 60)} dk`
                  : `${Math.round(staleSec / 3600)} sa`;
                const ageTone =
                  staleSec == null     ? 'neutral'
                  : staleSec < 300     ? 'ok'
                  : staleSec < 1800    ? 'warn'
                  : 'crit';
                const pulseColor = ageTone === 'ok'   ? '#34d399' // emerald-400
                                 : ageTone === 'warn' ? '#fbbf24' // amber-400
                                 : ageTone === 'crit' ? '#f87171' // red-400
                                 :                      '#9ca3af'; // gray-400
                return (
                  <div className="flex flex-wrap items-center justify-center md:justify-end gap-3.5 text-[11px] mono tabular-nums">
                    {/* Live Indicator Badges */}
                    <div className="flex items-center gap-2 px-2.5 py-1 rounded-full border" style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--line-strong)' }}>
                      <span className="relative flex h-1.5 w-1.5">
                        {ageTone === 'ok' && (
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        )}
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: pulseColor }}></span>
                      </span>
                      <span style={{ color: 'var(--text-2)', fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em' }}>
                        {ageTone === 'ok' ? 'BAĞLI' : ageTone === 'warn' ? 'GECİKME' : 'BAĞLANTI YOK'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-3)' }}>
                      <span style={{ color: 'var(--text-2)' }}>
                        {lastUpdated ? lastUpdated : 'Veri bekleniyor…'}
                      </span>
                      {ageLabel && (
                        <>
                          <span style={{ color: 'var(--text-4)' }}>·</span>
                          <span style={{ color: 'var(--text-3)' }}>{ageLabel} önce</span>
                        </>
                      )}
                      {dataSourceLabel && (
                        <>
                          <span style={{ color: 'var(--text-4)' }}>·</span>
                          <span className="px-2 py-0.5 rounded text-[10px] uppercase font-semibold border" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'var(--line)', color: 'var(--text-2)', letterSpacing: '0.05em' }}>
                            {dataSourceLabel}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </footer>
        )}
      </div>

      <PWAInstallPrompt />
      <PWAUpdateBanner />
      <KeyboardShortcutsHint />

      {/* Command Palette */}
      <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />

      {/* Mobile bottom navigation */}
      <MobileBottomNav
        unreadAlerts={unreadAlertCount}
        onAlertsClick={() => navigate('/uyarilar')}
        onSettingsClick={() => navigate('/ayarlar')}
        onLogout={!bypassed && session ? logout : undefined}
        userEmail={session?.email}
      />

      {/* ── Klavye kısayolları overlay ── */}
      {showShortcuts && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center animate-backdrop" style={{ background: 'rgba(10,11,14,0.7)', backdropFilter: 'blur(6px)' }} onClick={() => setShowShortcuts(false)}>
          <div className="card animate-popup-desktop p-6 w-[480px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
            <div className="flex items-baseline gap-2 mb-5">
              <span className="display text-lg" style={{ color: 'var(--text-primary)' }}>Klavye Kısayolları</span>
              <span className="ml-auto kbd">esc</span>
            </div>
            <div className="grid grid-cols-3 gap-x-6 gap-y-4">
              <div>
                <div className="eyebrow mb-2.5" style={{ color: 'var(--text-2)' }}>Navigasyon</div>
                <div className="space-y-2 text-[12px]">
                  {[['1','Komuta'],['2','Üretim'],['3','İklim'],['4','Analitik'],['5','Operasyon'],['6','Sistem'],['7','Ayarlar']].map(([k,v]) => (
                    <div key={k} className="flex items-center gap-2.5"><span className="kbd">{k}</span><span style={{ color: 'var(--text-2)' }}>{v}</span></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2.5" style={{ color: 'var(--text-2)' }}>Paneller</div>
                <div className="space-y-2 text-[12px]">
                  {[['S','Ayarlar'],['A','Uyarılar'],['F','Faz seçici']].map(([k,v]) => (
                    <div key={k} className="flex items-center gap-2.5"><span className="kbd">{k}</span><span style={{ color: 'var(--text-2)' }}>{v}</span></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2.5" style={{ color: 'var(--text-2)' }}>Eylem</div>
                <div className="space-y-2 text-[12px]">
                  {[['.','Tüm cihazları durdur'],['R','Son cihazları geri yükle'],['?','Bu ekran'],['esc','Kapat']].map(([k,v]) => (
                    <div key={k} className="flex items-center gap-2.5"><span className="kbd">{k}</span><span style={{ color: 'var(--text-2)' }}>{v}</span></div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 pt-4 text-[11px]" style={{ borderTop: '1px solid var(--line)', color: 'var(--text-3)' }}>
              Input odaklandığında kısayollar otomatik devre dışı kalır.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Header panel icon button — tekrar eden onMouseEnter/Leave handler'ın temiz versiyonu ──
const HeaderIconButton = memo(function HeaderIconButton({
  onClick, title, children,
}: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="w-9 h-9 flex items-center justify-center rounded-lg transition-all cursor-pointer"
      style={{ color: 'var(--text-3)' }}
      onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-3)'; e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
});
